/**
 * The single list of environment variables the build refuses to proceed
 * without. Imported by `vite.config.ts` (which enforces it at config time)
 * and by `env.test.ts` (which enforces that this list and `env.ts` agree).
 *
 * It lives in its own module so those two cannot drift: a name added to
 * `required()` in env.ts without appearing here fails a test, rather than
 * silently deferring the failure to whenever someone first imports `env`.
 *
 * Scoped to variables that have values today. Add each pending credential
 * here in the same commit that adds its value to the three mode files.
 */
export const REQUIRED = [
  'VITE_API_BASE',
  // C2, landed 2026-08-06. These four and their values in the three mode files
  // are ONE commit deliberately: `vite.config.ts`'s `tracke5:require-env` fails
  // the build at config time on a missing name, so the names without the values
  // break every build — and the values without the names leave `pending()`
  // throwing in the browser for a credential that now exists.
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_APP_ID',
] as const

/**
 * Variables `env.ts` reads but that cannot be required yet, because no value
 * exists. Each throws a named error on access rather than yielding undefined.
 *
 * Moving a name from here to REQUIRED is the whole ceremony for landing a
 * credential — the test enforces that every name in env.ts appears in exactly
 * one of the two lists.
 */
export const PENDING = {
  VITE_REVENUECAT_WEB_KEY: 'C1',
} as const

/**
 * Variables whose ABSENCE is a supported state, forever: the feature is off
 * without them and a missing value must never fail a build or a deploy (#112).
 * Not a waiting room — nothing moves from here to REQUIRED, so there is no
 * ceremony and `env.optional()` returns `undefined` rather than throwing.
 * Setting one in the three mode files is all it takes to switch the feature on.
 * `env.test.ts` requires every `optional('…')` in env.ts to be listed here.
 */
export const OPTIONAL = {
  VITE_SENTRY_DSN: 'C3',
  VITE_FIREBASE_MEASUREMENT_ID: 'C4',
} as const

/**
 * REQUIRED names whose values are NOT in the mode files.
 *
 * They are GitHub repository secrets, exported by the build step in
 * `.github/workflows/{ci,deploy-dev}.yml`. Vite's `loadEnv` merges
 * `process.env` entries carrying the `VITE_` prefix, so `tracke5:require-env`
 * and `import.meta.env` both see them without a file being written.
 *
 * **This list is an exemption from a guard, so it carries its own.**
 * `env.test.ts` asserts every REQUIRED name has a real value in every mode file
 * — the check that stops a name being added to REQUIRED without a value and
 * breaking that mode's build. Moving a credential to CI would defeat it, so the
 * test instead requires each name here to be *exported by both workflows*. An
 * entry that nothing supplies fails, which keeps the exemption honest rather
 * than making it a hole.
 *
 * Not a secrecy measure: every VITE_ var is embedded in the bundle and the
 * Firebase apiKey is public by design (CLAUDE.md). What it buys is that the
 * repo stops carrying credentials — which is what lets #177 give production its
 * own Firebase project without those values ever entering git.
 */
export const CI_INJECTED = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_APP_ID',
] as const
